import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  UserCheck, UserX, Clock, Eye, FileText, Building2, User, 
  Mail, Phone, MapPin, CheckCircle2, XCircle, AlertCircle,
  Loader2, ExternalLink, Download, Calendar, Store, CreditCard, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { admin } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import SellerCountryBadge, { getSellerCountryMeta } from '@/components/admin/SellerCountryBadge';

const SellerVerificationTab = ({ onSellersUpdated }) => {
  const { t } = useTranslation();
  
  const [pendingSellers, setPendingSellers] = useState([]);
  const [allSellers, setAllSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [statusFilter, setStatusFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');

  useEffect(() => {
    loadSellers();
  }, []);

  const loadSellers = async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes] = await Promise.all([
        admin.pendingSellers(),
        admin.allSellers()
      ]);
      setPendingSellers(pendingRes.data.sellers || []);
      setAllSellers(allRes.data.sellers || []);
    } catch (error) {
      console.error('Failed to load sellers:', error);
      toast.error(t('admin.sellers.loadError'));
    } finally {
      setLoading(false);
      onSellersUpdated?.();
    }
  };

  const handleApprove = async (seller) => {
    setProcessing(true);
    try {
      await admin.approveSeller(seller.id);
      toast.success(t('admin.sellers.approved'));
      loadSellers();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.sellers.approveError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSeller) return;
    
    setProcessing(true);
    try {
      await admin.rejectSeller(selectedSeller.id, rejectReason || 'Eksik veya hatalı belgeler');
      toast.success(t('admin.sellers.rejected'));
      loadSellers();
      setShowRejectDialog(false);
      setShowDetailDialog(false);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('admin.sellers.rejectError'));
    } finally {
      setProcessing(false);
    }
  };

  const openSellerDetail = (seller) => {
    setSelectedSeller(seller);
    setShowDetailDialog(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />{t('admin.sellers.statusApproved')}</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />{t('admin.sellers.statusRejected')}</Badge>;
      case 'pending':
      default:
        return <Badge variant="secondary" className="bg-yellow-500 text-white"><Clock className="w-3 h-3 mr-1" />{t('admin.sellers.statusPending')}</Badge>;
    }
  };

  const getSellerTypeBadge = (type) => {
    if (type === 'company' || type === 'corporate') {
      return <Badge variant="outline"><Building2 className="w-3 h-3 mr-1" />{t('admin.sellers.typeCompany')}</Badge>;
    }
    return <Badge variant="outline"><User className="w-3 h-3 mr-1" />{t('admin.sellers.typeIndividual')}</Badge>;
  };

  const getCategoryBadge = (category) => {
    const categories = {
      'supermarket': { label: 'Süpermarket', icon: Store },
      'restaurant': { label: 'Restoran', icon: Store },
      'car_rental': { label: 'Araç Kiralama', icon: Store },
      'cars': { label: 'Araç Kiralama', icon: Store },
      'hotel': { label: 'Otel', icon: Store },
      'taxi-limousine': { label: 'Taksi & Limuzin', icon: Store },
      'taxi_limousine': { label: 'Taksi & Limuzin', icon: Store },
      'limousine': { label: 'Limuzin', icon: Store },
    };
    const cat = categories[category] || { label: category, icon: Store };
    return <Badge variant="outline"><cat.icon className="w-3 h-3 mr-1" />{cat.label}</Badge>;
  };

  const filteredSellers = [...allSellers.filter((s) => {
    if (statusFilter !== 'all' && s.seller_status !== statusFilter) return false;
    const meta = getSellerCountryMeta(s.country);
    if (countryFilter === 'tr') return meta.isTR;
    if (countryFilter === 'international') return !meta.missing && !meta.isTR;
    if (countryFilter === 'missing') return meta.missing;
    return true;
  })].sort((a, b) => {
    const ta = new Date(a.created_at || a.updated_at || 0).getTime();
    const tb = new Date(b.created_at || b.updated_at || 0).getTime();
    return tb - ta;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-yellow-100">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-700">{pendingSellers.length}</p>
                <p className="text-sm text-yellow-600">{t('admin.sellers.pending')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-green-100">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-700">
                  {allSellers.filter(s => s.seller_status === 'approved').length}
                </p>
                <p className="text-sm text-green-600">{t('admin.sellers.approved')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-700">
                  {allSellers.filter(s => s.seller_status === 'rejected').length}
                </p>
                <p className="text-sm text-red-600">{t('admin.sellers.rejected')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-blue-100">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">{allSellers.length}</p>
                <p className="text-sm text-blue-600">{t('admin.sellers.total')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Sellers Alert */}
      {pendingSellers.length > 0 && (
        <Alert className="border-yellow-300 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">{t('admin.sellers.pendingAlert')}</AlertTitle>
          <AlertDescription className="text-yellow-700">
            {pendingSellers.length} {t('admin.sellers.pendingAlertDesc')}
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t('admin.sellers.pendingTab')} ({pendingSellers.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-2">
            <Store className="w-4 h-4" />
            {t('admin.sellers.allTab')} ({allSellers.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Sellers Tab */}
        <TabsContent value="pending">
          {pendingSellers.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-medium">{t('admin.sellers.noPending')}</h3>
                <p className="text-muted-foreground">{t('admin.sellers.noPendingDesc')}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pendingSellers.map((seller) => (
                <Card key={seller.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <Avatar className="w-12 h-12">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {seller.name?.charAt(0)?.toUpperCase() || 'S'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h3 className="font-semibold text-lg">{seller.name || seller.company_name || 'İsimsiz Satıcı'}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                            <Mail className="w-4 h-4" />
                            {seller.email}
                          </div>
                          {seller.phone && (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                              <Phone className="w-4 h-4" />
                              {seller.phone}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {getSellerTypeBadge(seller.seller_type)}
                            {seller.seller_category && getCategoryBadge(seller.seller_category)}
                            {getStatusBadge(seller.seller_status)}
                            <SellerCountryBadge country={seller.country} compact />
                            {seller.email_verified ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" /> Email Doğrulandı
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                                <AlertCircle className="w-3 h-3 mr-1" /> Email Doğrulanmadı
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                            <Calendar className="w-3 h-3" />
                            {t('admin.sellers.appliedAt')}: {formatDate(seller.created_at)}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => openSellerDetail(seller)}>
                          <Eye className="w-4 h-4 mr-1" />
                          {t('admin.sellers.viewDetails')}
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(seller)}
                          disabled={processing}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          {t('admin.sellers.approve')}
                        </Button>
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={() => {
                            setSelectedSeller(seller);
                            setShowRejectDialog(true);
                          }}
                          disabled={processing}
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          {t('admin.sellers.reject')}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* All Sellers Tab */}
        <TabsContent value="all">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('admin.sellers.allSellers')}</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-sm">{t('admin.sellers.filterByStatus')}:</Label>
                  <select 
                    className="border rounded px-2 py-1 text-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">{t('admin.sellers.all')}</option>
                    <option value="pending">{t('admin.sellers.statusPending')}</option>
                    <option value="approved">{t('admin.sellers.statusApproved')}</option>
                    <option value="rejected">{t('admin.sellers.statusRejected')}</option>
                  </select>
                  <Label className="text-sm">{t('admin.sellers.filterByCountry')}:</Label>
                  <select
                    className="border rounded px-2 py-1 text-sm"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <option value="all">{t('admin.sellers.all')}</option>
                    <option value="tr">{t('admin.sellers.filterTurkey')}</option>
                    <option value="international">{t('admin.sellers.filterInternational')}</option>
                    <option value="missing">{t('admin.sellers.filterMissing')}</option>
                  </select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.sellers.name')}</TableHead>
                    <TableHead>{t('admin.sellers.email')}</TableHead>
                    <TableHead>{t('admin.sellers.country')}</TableHead>
                    <TableHead>{t('admin.sellers.type')}</TableHead>
                    <TableHead>{t('admin.sellers.category')}</TableHead>
                    <TableHead>{t('admin.sellers.status')}</TableHead>
                    <TableHead>{t('admin.sellers.date')}</TableHead>
                    <TableHead>{t('admin.sellers.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSellers.map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell className="font-medium">
                        {seller.name || seller.company_name || '-'}
                      </TableCell>
                      <TableCell>{seller.email}</TableCell>
                      <TableCell>
                        <SellerCountryBadge country={seller.country} compact />
                      </TableCell>
                      <TableCell>{getSellerTypeBadge(seller.seller_type)}</TableCell>
                      <TableCell>
                        {seller.seller_category ? getCategoryBadge(seller.seller_category) : '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(seller.seller_status)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(seller.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => openSellerDetail(seller)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Seller Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              {t('admin.sellers.sellerDetails')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.sellers.reviewApplication')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedSeller && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t('admin.sellers.basicInfo')}
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.name')}:</span>
                      <span className="font-medium">{selectedSeller.name || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.email')}:</span>
                      <span className="font-medium">{selectedSeller.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.phone')}:</span>
                      <span className="font-medium">{selectedSeller.phone || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.type')}:</span>
                      {getSellerTypeBadge(selectedSeller.seller_type)}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.category')}:</span>
                      {selectedSeller.seller_category ? getCategoryBadge(selectedSeller.seller_category) : '-'}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t('admin.sellers.status')}:</span>
                      {getStatusBadge(selectedSeller.seller_status)}
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-muted-foreground">{t('admin.sellers.country')}:</span>
                      <SellerCountryBadge country={selectedSeller.country} compact />
                    </div>
                  </div>
                </div>

                {/* Company Info (if applicable) */}
                {(selectedSeller.seller_type === 'company' || selectedSeller.seller_type === 'corporate') && (
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t('admin.sellers.companyInfo')}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.companyName')}:</span>
                        <span className="font-medium">{selectedSeller.company_name || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.taxNumber')}:</span>
                        <span className="font-medium">{selectedSeller.company_tax_number || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.companyAddress')}:</span>
                        <span className="font-medium text-right max-w-[200px]">{selectedSeller.company_address || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* Address Info */}
              {(selectedSeller.address || selectedSeller.city || selectedSeller.country || selectedSeller.full_address || selectedSeller.district) && (
                <>
                  <div className="space-y-4">
                    <h4 className="font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {t('admin.sellers.addressInfo')}
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.address')}:</span>
                        <span className="font-medium">{selectedSeller.address || selectedSeller.full_address || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.city')}:</span>
                        <span className="font-medium">{selectedSeller.city || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('admin.sellers.district')}:</span>
                        <span className="font-medium">{selectedSeller.district || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-muted-foreground">{t('admin.sellers.country')}:</span>
                        <SellerCountryBadge country={selectedSeller.country} compact />
                      </div>
                    </div>
                  </div>
                  <Separator />
                </>
              )}

              {/* Documents */}
              <div className="space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('admin.sellers.documents')}
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedSeller.id_document_url && (
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <span className="text-sm font-medium">{t('admin.sellers.idDocument')}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={selectedSeller.id_document_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            {t('admin.sellers.view')}
                          </a>
                        </Button>
                      </div>
                    </Card>
                  )}
                  {selectedSeller.trade_register_doc_url && (
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium">{t('admin.sellers.tradeRegister')}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={selectedSeller.trade_register_doc_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            {t('admin.sellers.view')}
                          </a>
                        </Button>
                      </div>
                    </Card>
                  )}
                  {selectedSeller.authorized_person_id_url && (
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-purple-600" />
                          <span className="text-sm font-medium">{t('admin.sellers.authorizedPersonId')}</span>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <a href={selectedSeller.authorized_person_id_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4 mr-1" />
                            {t('admin.sellers.view')}
                          </a>
                        </Button>
                      </div>
                    </Card>
                  )}
                  {!selectedSeller.id_document_url && !selectedSeller.trade_register_doc_url && !selectedSeller.authorized_person_id_url && (
                    <p className="text-sm text-muted-foreground col-span-2">{t('admin.sellers.noDocuments')}</p>
                  )}
                </div>
              </div>

              {/* Rejection reason if rejected */}
              {selectedSeller.seller_status === 'rejected' && selectedSeller.rejection_reason && (
                <>
                  <Separator />
                  <Alert variant="destructive">
                    <XCircle className="h-4 w-4" />
                    <AlertTitle>{t('admin.sellers.rejectionReason')}</AlertTitle>
                    <AlertDescription>{selectedSeller.rejection_reason}</AlertDescription>
                  </Alert>
                </>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              {t('common.close')}
            </Button>
            {selectedSeller?.seller_status === 'pending' && (
              <>
                <Button 
                  variant="destructive"
                  onClick={() => {
                    setShowRejectDialog(true);
                  }}
                  disabled={processing}
                >
                  <UserX className="w-4 h-4 mr-1" />
                  {t('admin.sellers.reject')}
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleApprove(selectedSeller)}
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <UserCheck className="w-4 h-4 mr-1" />
                  )}
                  {t('admin.sellers.approve')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <UserX className="w-5 h-5" />
              {t('admin.sellers.rejectSeller')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.sellers.rejectDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('admin.sellers.rejectionReason')}</Label>
              <Textarea
                placeholder={t('admin.sellers.rejectionPlaceholder')}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="text-sm text-muted-foreground">
              {t('admin.sellers.rejectionNote')}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleReject}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <UserX className="w-4 h-4 mr-1" />
              )}
              {t('admin.sellers.confirmReject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SellerVerificationTab;
